import { Action } from '@anm/components/ActionsDropdown';
import EditableField from '@anm/components/EditableField';
import { DEFAULT_DROPDOWN_DELAY } from '@anm/constants/delays';
import { DEFAULT_PROJECT_NAME } from '@anm/constants/my-videos';
import getPassedTime from '@anm/helpers/getPassedTime';
import noop from '@anm/helpers/noop';
import formatTime from '@anm/helpers/time/formatTime';
import useHover from '@anm/hooks/useHover';
import useSwitchState from '@anm/hooks/useSwitchState';
import { memo, FC, RefObject } from 'react';

import ProgressBar from '../ProgressBar';
import ProgressiveVideoPreview from '../video/ProgressiveVideoPreview';

import PlayerStatuses, { DRAFT, EMBEDDED, Statuses } from './PlayerStatuses';
import ProjectPreviewModal from './ProjectPreviewModal';
import {
  CenteredLoader,
  LoaderOverlay,
  LoaderWrapper,
  PlayerActionsDropdown,
  PlayerBottom,
  PlayerDescription,
  PlayerDuration,
  PlayerModifiedDateAndActionsWrapper,
  PlayerPreview,
  PlayerStatusesWrapper,
  PlayerTag,
  PlayerTitleBlock,
  PlayerWrapper,
  ProgressWrapper,
  TopPlayerStatusesWrapper
} from './Wrapper';
import { arePlayerBlockPropsEqual } from './helpers';
import { useDropdownDirection } from './hooks';
import { VideoSizes } from '@anm/hosting-player';

export { DRAFT, EMBEDDED };
const uploadingSubtitle = 'Uploading...';

export type PlayerBlockProps = {
  title: string;
  width: number;
  height: number;
  hasTag: boolean;
  actions: Action[];
  modified: number;
  showProgress: boolean;
  uploadProgress: number | null;
  onFieldFocused: (v: boolean) => void;
  renderEditLink: (children: JSX.Element) => JSX.Element;
  preview?: string;
  streamId?: string | null;
  statuses?: Statuses;
  hostedId?: string;
  videoSrc?: string | null;
  duration?: number;
  bottomRef?: RefObject<HTMLDivElement>;
  isHovered?: boolean;
  isPending?: boolean;
  canEdit?: boolean;
  canStreamVideo?: boolean;
  actionsDividerIndex?: number;
  onRename?: (value: string) => void;
  onPreviewClick?: () => void;
  onUploadCancel?: (uploadId: string) => void;
  renderEmbedLink?: (children: JSX.Element) => JSX.Element;
  renderVideoPlayer?: (sizes?: VideoSizes) => JSX.Element;
  renderLandingLink?: (children: JSX.Element) => JSX.Element;
  renderPermissionLink?: (children: JSX.Element) => JSX.Element;
};

const PlayerBlock: FC<PlayerBlockProps> = ({
  title,
  width,
  height,
  hasTag,
  actions,
  preview,
  modified,
  duration,
  statuses = {},
  videoSrc,
  hostedId,
  bottomRef,
  isHovered: isOutHovered = null,
  isPending: isDuplicationPending,
  showProgress,
  renderEditLink,
  renderEmbedLink,
  uploadProgress,
  onRename = noop,
  onFieldFocused,
  onPreviewClick,
  canEdit = true,
  onUploadCancel = noop,
  renderLandingLink,
  renderVideoPlayer,
  actionsDividerIndex,
  renderPermissionLink
}) => {
  const [showPreviewModal, openPreviewModal, closePreviewModal] = useSwitchState();
  const [hoverRef, isOwnHovered] = useHover<HTMLDivElement>();
  const [dropdownWrapperRef, dropdownDirection] = useDropdownDirection(actions.length);
  const isHovered = isOutHovered === null ? isOwnHovered : isOutHovered;

  const playerDescription = showProgress ? uploadingSubtitle : getPassedTime(modified);
  const is3DotsDisabled = showProgress || isDuplicationPending;

  return (
    <PlayerWrapper ref={hoverRef}>
      {showPreviewModal && (
        <ProjectPreviewModal
          width={width}
          height={height}
          actions={actions}
          renderEditLink={renderEditLink}
          renderEmbedLink={renderEmbedLink}
          renderLandingLink={renderLandingLink}
          renderPermissionLink={renderPermissionLink}
          title={title}
          hostedId={hostedId}
          statuses={statuses}
          preview={preview}
          onModalClose={closePreviewModal}
          renderVideoPlayer={renderVideoPlayer}
        />
      )}
      <TopPlayerStatusesWrapper>
        <PlayerStatuses statuses={{ isDraft: statuses.isDraft }} {...{ hostedId, renderEditLink, renderEmbedLink }} />
      </TopPlayerStatusesWrapper>
      <PlayerPreview>
        {isDuplicationPending && (
          <LoaderWrapper>
            <LoaderOverlay>
              <CenteredLoader size="large" color="white" />
            </LoaderOverlay>
          </LoaderWrapper>
        )}
        {typeof duration === 'number' && <PlayerDuration>{formatTime(duration)}</PlayerDuration>}
        {showProgress && (
          <ProgressWrapper>
            <ProgressBar value={uploadProgress!} variant="radial" onCancel={() => onUploadCancel(hostedId!)} />
          </ProgressWrapper>
        )}
        <div
          onClick={() => {
            openPreviewModal();
            onPreviewClick?.();
          }}
        >
          <ProgressiveVideoPreview {...{ videoSrc, width, height, isHovered }} alt={title} previewSrc={preview} />
        </div>

        {hasTag && <PlayerTag>Template</PlayerTag>}
      </PlayerPreview>
      <PlayerBottom ref={bottomRef}>
        <PlayerTitleBlock disabled={showProgress || isDuplicationPending}>
          <EditableField
            variant="cropMultiline"
            hoverVariant="pencil"
            isActive={false}
            placeholder="Add title"
            isFocusedOnClick={canEdit}
            initialValue={title || DEFAULT_PROJECT_NAME}
            onChange={onRename}
            onFieldFocused={onFieldFocused}
          />
        </PlayerTitleBlock>
        <PlayerStatusesWrapper>
          <PlayerStatuses
            statuses={{
              isProtected: statuses.isProtected,
              isEmbedded: statuses.isEmbedded,
              isLanding: statuses.isLanding
            }}
            {...{
              hostedId,
              renderEditLink,
              renderEmbedLink,
              renderLandingLink,
              renderPermissionLink
            }}
          />
        </PlayerStatusesWrapper>
        <PlayerModifiedDateAndActionsWrapper>
          <PlayerDescription>{playerDescription}</PlayerDescription>
          <div ref={dropdownWrapperRef} data-dropdown="file dropdown">
            <PlayerActionsDropdown
              actions={actions}
              openDelay={DEFAULT_DROPDOWN_DELAY}
              closeDelay={DEFAULT_DROPDOWN_DELAY}
              dividerIndexes={[actionsDividerIndex]}
              direction={dropdownDirection}
              isDisabledButton={is3DotsDisabled}
            />
          </div>
        </PlayerModifiedDateAndActionsWrapper>
      </PlayerBottom>
    </PlayerWrapper>
  );
};

const PlayerBlockMemorized = memo(PlayerBlock, arePlayerBlockPropsEqual);

export default PlayerBlockMemorized;
